import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { Icon, Checkbox, Spin, Tag, Tooltip } from 'antd';
import { COL, TABLE_W, SORTERS, formatCNPJ } from './tableColumns';

var thBase = {
    padding: '8px 12px', background: '#fafafa',
    borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8',
    borderRight: '1px solid #e8e8e8',
    fontWeight: 500, textAlign: 'left', whiteSpace: 'nowrap',
    overflow: 'hidden', textOverflow: 'ellipsis'
};
var tdBase = {
    padding: '8px 12px', background: '#fff',
    borderBottom: '1px solid #e8e8e8', borderRight: '1px solid #e8e8e8',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
};

var stickyLeftTh = {
    position: 'sticky', left: 0, zIndex: 3,
    borderLeft: '1px solid #e8e8e8'
};
var stickyLeftTd = {
    position: 'sticky', left: 0, zIndex: 2,
    borderLeft: '1px solid #e8e8e8'
};

function thS(w, extra) { return Object.assign({}, thBase, { minWidth: w }, extra); }
function tdS(w, extra) { return Object.assign({}, tdBase, { minWidth: w }, extra); }

var TableList = React.memo(function TableList(props) {
    var isLoading = props.isLoading;
    var filteredData = props.filteredData;
    var rowSelection = props.rowSelection;

    var sortState = React.useState('customer_cod');
    var sortField = sortState[0]; var setSortField = sortState[1];
    var orderState = React.useState('ascend');
    var sortOrder = orderState[0]; var setSortOrder = orderState[1];

    var processedData = React.useMemo(function() {
        var src = filteredData || [];
        if (sortField && SORTERS[sortField]) {
            src = src.slice().sort(SORTERS[sortField]);
            if (sortOrder === 'descend') src.reverse();
        }
        return src;
    }, [filteredData, sortField, sortOrder]);

    function toggleSort(field) {
        if (sortField === field) {
            if (sortOrder === 'ascend') { setSortOrder('descend'); }
            else { setSortField(null); setSortOrder(null); }
        } else {
            setSortField(field); setSortOrder('ascend');
        }
    }

    function sortIcon(field) {
        if (sortField !== field) return React.createElement(Icon, { type: 'caret-down', style: { marginLeft: 4, fontSize: 10, opacity: 0.3 } });
        return React.createElement(Icon, {
            type: sortOrder === 'ascend' ? 'caret-up' : 'caret-down',
            style: { marginLeft: 4, fontSize: 10, color: '#1890ff' }
        });
    }

    var selectedKeys = rowSelection.selectedRowKeys;
    var selectedKeysSet = React.useMemo(function() { return new Set(selectedKeys); }, [selectedKeys]);
    var allChecked = processedData.length > 0 && selectedKeys.length === processedData.length;
    var someChecked = selectedKeys.length > 0 && !allChecked;

    function onToggleAll(e) {
        if (e.target.checked) {
            rowSelection.onChange(processedData.map(function(r) { return r.id; }));
        } else {
            rowSelection.onChange([]);
        }
    }
    function onToggleRow(id) {
        var idx = selectedKeys.indexOf(id);
        if (idx === -1) { rowSelection.onChange(selectedKeys.concat([id])); }
        else { rowSelection.onChange(selectedKeys.filter(function(k) { return k !== id; })); }
    }

    var dataRef = React.useRef({});
    dataRef.current = { processedData: processedData };

    var tableComponents = React.useMemo(function() {
        return {
            Table: function(p) {
                return React.createElement('table', Object.assign({}, p, {
                    style: Object.assign({}, p.style, {
                        borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: TABLE_W
                    })
                }));
            },
            TableRow: function(p) {
                return React.createElement('tr', Object.assign({}, p, {
                    className: 'row-clabs' + (p.className ? ' ' + p.className : '')
                }));
            }
        };
    }, []);

    var fixedHeaderContent = React.useCallback(function() {
        return (
            <tr>
                <th style={thS(COL.check, Object.assign({ textAlign: 'center' }, stickyLeftTh))}>
                    <Checkbox checked={allChecked} indeterminate={someChecked} onChange={onToggleAll} />
                </th>
                <th style={thS(COL.cod, { cursor: 'pointer' })} onClick={function() { toggleSort('customer_cod'); }}>
                    Cód {sortIcon('customer_cod')}
                </th>
                <th style={thS(COL.name, { cursor: 'pointer' })} onClick={function() { toggleSort('customer_name'); }}>
                    Empresa {sortIcon('customer_name')}
                </th>
                <th style={thS(COL.cnpj)}>CNPJ</th>
                <th style={thS(COL.partner, { cursor: 'pointer' })} onClick={function() { toggleSort('partner_name'); }}>
                    Sócio {sortIcon('partner_name')}
                </th>
                <th style={thS(COL.cpf)}>CPF</th>
                <th style={thS(COL.pct, { cursor: 'pointer', textAlign: 'center' })} onClick={function() { toggleSort('partner_percentage'); }}>
                    % {sortIcon('partner_percentage')}
                </th>
                <th style={thS(COL.phone)}>WhatsApp</th>
            </tr>
        );
    }, [allChecked, someChecked, sortField, sortOrder]);

    function itemContent(index, record) {
        var isSelected = selectedKeysSet.has(record.id);
        var cleanPhone = record.partner_phone ? record.partner_phone.replace(/\D/g, '') : '';

        return (
            <>
                <td style={tdS(COL.check, Object.assign({ textAlign: 'center' }, stickyLeftTd))}>
                    <Checkbox checked={isSelected} onChange={function() { onToggleRow(record.id); }} />
                </td>
                <td style={tdS(COL.cod, { textAlign: 'center' })}>{record.customer_cod}</td>
                <td style={tdS(COL.name)}>{record.customer_name}</td>
                <td style={tdS(COL.cnpj)}>{formatCNPJ(record.customer_cnpj)}</td>
                <td style={tdS(COL.partner)}>
                    {record.partner_name}
                    {record.partner_is_legal_representative && (
                        <Tooltip title="Representante Legal">
                            <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>RL</Tag>
                        </Tooltip>
                    )}
                </td>
                <td style={tdS(COL.cpf)}>{record.partner_cpf || '-'}</td>
                <td style={tdS(COL.pct, { textAlign: 'center' })}>{record.partner_percentage ? record.partner_percentage + '%' : '-'}</td>
                <td style={tdS(COL.phone)}>
                    {record.partner_phone ? (
                        <a href={'https://wa.me/55' + cleanPhone} target="_blank" rel="noopener noreferrer">
                            {record.partner_phone}
                        </a>
                    ) : '-'}
                </td>
            </>
        );
    }

    return (
        <Spin spinning={isLoading}>
            <TableVirtuoso
                style={{ height: window.innerHeight - 235, border: '1px solid #e8e8e8' }}
                data={processedData}
                components={tableComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={itemContent}
                overscan={20}
            />
            <div style={{ padding: '8px 16px', background: '#fafafa', border: '1px solid #e8e8e8', borderTop: 'none', fontSize: 13 }}>
                Total: {processedData.length} sócio(s)
            </div>
        </Spin>
    );
});

export default TableList;
